/**
 * Returns focusable children within a given container element.
 *
 * @param {HTMLElement} el - The container element to search within
 * @returns {HTMLElement[]} An array of focusable child elements
 */
export const queryFocusableChildren = (el) => {
  const targets = [
    'button',
    '[href]',
    'input:not([type="hidden"])',
    'select',
    'textarea',
    '[tabindex]',
  ]
    .map((s) => `${s}:not([tabindex="-1"]):not([disabled])`)
    .join(', ');
  return [...el.querySelectorAll(targets)];
};

/**
 * Traps keyboard focus within a given container element.
 *
 * @param {HTMLElement} container - The DOM element within which to trap focus
 * @param {boolean} [options.clickOutsideDeactivates=false] - If true, clicking outside the container
 *        deactivates the focus trap and allows the click event to propagate normally.
 *        Otherwise clicking on interactive elements outside focus-trap might not fire click event.
 * @param {boolean} [options.initialFocus=true] - If true, immidiately sets focus into the focus trap
 *        (currently to container element). False - disables this behavior.
 * @param {boolean} [options.returnFocus=true] - If true, returns focus to the element that was in focus before
 *        creating the focus trap. False - disables this behavior.
 * @returns {() => void} A cleanup function that removes the focus trap
 */
export const trapFocus = (
  container,
  { clickOutsideDeactivates = false, returnFocus = true, initialFocus = true } = {},
) => {
  let returnFocusElement = document.activeElement;

  const handleReturnFocus = () => {
    if (
      !returnFocusElement ||
      [document.documentElement, document.body].includes(returnFocusElement)
    ) {
      return;
    }

    setTimeout(() => {
      returnFocusElement.focus();
    });
  };

  const handleFocus = (e) => {
    const before = e.relatedTarget;
    const after = e.target;

    if (before === after || container.contains(after)) {
      return;
    }

    const focusable = queryFocusableChildren(container);
    if (!focusable.length) return;

    const firstElement = focusable.at(0);
    const lastElement = focusable.at(-1);

    // prevents scroll to elements outside the trap container in Chromium browsers
    window.scrollTo({ top: window.scrollY, behavior: 'auto' });

    if (before === firstElement) {
      lastElement.focus();
    } else {
      firstElement.focus();
    }
  };

  const handlePointerDown = (e) => {
    if (clickOutsideDeactivates && !container.contains(e.target)) {
      deactivate();
    }
  };

  const deactivate = () => {
    if (returnFocus) {
      handleReturnFocus();
    }

    document.removeEventListener('focusin', handleFocus);
    if (clickOutsideDeactivates) {
      document.removeEventListener('pointerdown', handlePointerDown);
    }
  };

  if (initialFocus) {
    container.focus();
  }

  document.addEventListener('focusin', handleFocus);
  if (clickOutsideDeactivates) {
    document.addEventListener('pointerdown', handlePointerDown);
  }

  return deactivate;
};
